import { DEFAULT_RIBBON_OPACITY, } from "@diality/contracts";
import { HORIZONTAL_ENDCAP_STATES, endcapSteps } from "./presets/endcap";
import { ribbonOpening } from "./presets";
/**
 * Assemble the composition a page draws from its opening and ending.
 *
 * An opening is one of the prototype's authored compositions (its initial shape,
 * curve, palette and the flood / chapter / settle steps it knows). The ending
 * decides whether the landing's horizontal hand-off is appended: for openings
 * that never authored one, the composer fades the shape the visitor is looking
 * at (the settled state if the opening has one, else the opening itself) by
 * synthesizing a `<state>-hidden` copy with `effectOpacity: 0`, then reuses the
 * landing's endcap states and steps — they are straight, so they do not care
 * which curve profile the opening carries.
 *
 * Memoized per key: the engine compares compositions by reference and recompiles
 * its shader variants when the reference changes.
 */
const cache = new Map();
export function compositionKey(opening, ending) {
    return `${opening}/${ending}`;
}
export function composeRibbon(opening, ending) {
    const key = compositionKey(opening, ending);
    const cached = cache.get(key);
    if (cached)
        return cached;
    const base = ribbonOpening(opening);
    const nonEndcap = base.steps.filter((step) => step.role !== "endcap");
    const states = [...base.states];
    const steps = [...nonEndcap];
    if (ending === "handoff") {
        const authored = base.steps.filter((step) => step.role === "endcap");
        if (authored.length > 0) {
            steps.push(...authored);
        }
        else {
            // Fade out in the shape the visitor last saw, then hand off.
            const settle = nonEndcap.find((step) => step.role === "settle");
            const lastId = (settle && "stateId" in settle && settle.stateId) || base.initialStateId;
            const last = states.find((state) => state.id === lastId) ?? states[0];
            const hiddenId = `${lastId}-hidden`;
            states.push({ ...last, id: hiddenId, effectOpacity: 0 });
            if (!states.some((state) => state.id === "horizontal-endcap")) {
                states.push(...HORIZONTAL_ENDCAP_STATES);
            }
            steps.push(...endcapSteps(hiddenId));
        }
    }
    else {
        // `hold` keeps the shape to the end: drop the endcap states too so the
        // shader variant set stays as small as the composition really is.
        for (let i = states.length - 1; i >= 0; i -= 1) {
            if (states[i].id === "horizontal-endcap" || states[i].id === "horizontal-endcap-hidden") {
                states.splice(i, 1);
            }
        }
    }
    const roles = Array.from(new Set(steps.map((step) => step.role)));
    const composition = { ...base, id: key, roles, states, steps };
    cache.set(key, composition);
    return composition;
}
/** The scene the engine draws for a page's plan. */
export function buildRibbonScene(plan, opacity = DEFAULT_RIBBON_OPACITY) {
    return {
        composition: composeRibbon(plan.opening, plan.ending),
        opacity,
        waypoints: plan.waypoints,
    };
}
//# sourceMappingURL=compose.js.map